import { FC, useEffect } from "react";
import { Typography, useTheme, MenuItem, Select, FormControl, SelectChangeEvent, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import useSignIn from "./hooks/useSignIn";
import { Button } from "components/common/Button";
import { StyledSignIn, StyledSignInHeadings } from "styles/features/sign-in";
import '../../i18n'; // Ensure this is correctly linked to your i18n setup
import { useSearchParams } from "react-router-dom";
import {CONSTANTS} from "../../constants/constants";
import { Alert } from "components/common/Alert";
import { StyledLoginError } from "styles/components/alert";

const SignIn: FC = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation(); // Initialize translation
  const { loading, handleSubmit } = useSignIn();

  const [searchParams] = useSearchParams();
  const data = searchParams.get('data');
  const errorMsgKey = data ? (CONSTANTS.ERROR_MSG_KEYS as any)[data]: null;


  // Set the direction based on the language when the component mounts
  useEffect(() => {
    const currentLanguage = i18n.language || localStorage.getItem('i18nextLng') || 'en';
    document.documentElement.dir = currentLanguage === 'en' ? 'ltr' : 'rtl';
  }, [i18n.language]);

  // Language change handler with the correct type
  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    const newLanguage = event.target.value as string;
    i18n.changeLanguage(newLanguage);
    localStorage.setItem('i18nextLng', newLanguage); // Optional: Persist language choice
    document.documentElement.dir = newLanguage === 'en' ? 'ltr' : 'rtl'; // Set direction based on language
  };

  return (
    <StyledSignIn style={{ direction: i18n.language === 'en' ? 'ltr' : 'rtl' }}> {/* Apply direction to the container */}
      <StyledSignInHeadings>
        <Typography variant="h1" color={theme.palette.common.black}>
          {t('welcome')} 
        </Typography>
        <Typography
          variant="bodyL"
          color={theme.palette.common.black}
          paragraph
          fontWeight={300}
          marginBottom={0}
        >
          {t('login_prompt')} 
        </Typography>

        {/* Container for language selection */}
        <Box mt={2} mb={2}>
          <FormControl variant="outlined" fullWidth>
            <Select
              value={i18n.language}
              onChange={handleLanguageChange}
              displayEmpty
              inputProps={{ 'aria-label': 'Without label' }}
              style={{ height: '56px' }} // Ensure consistent height
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="he">עברית</MenuItem>
              <MenuItem value="ar">العربية</MenuItem>
              {/* Add more languages here */}
            </Select>
          </FormControl>
        </Box>

        {/* Sign In Button */}
        <Button
          className="sign-in__btn"
          onClick={handleSubmit}
          disabled={loading}
          isLoading={loading}
          style={{ width: '100%', height: '56px' }} // Full width button
        >
          {t('log_in')} 
        </Button>
        {errorMsgKey &&
          <StyledLoginError>{t(errorMsgKey)}</StyledLoginError>
        }
      </StyledSignInHeadings>
    </StyledSignIn>
  );
};

export default SignIn;
